import {
  CHANNEL_NESTED_KEYS,
  getActiveChannelKey,
  getActiveSectionKey,
  getChannelLabel,
} from '@/components/filter/SaveFiltersDialog/SaveFiltersDialog.utils';
import { LOCAL_FILTERS_STORAGE_MAP } from '@/context/LocalFiltersRegistry/localFiltersKeys';
import { syncSessionStorage } from '@/hooks/useSessionStorage';
import { FILTER_CONFIG } from '@/models';
import { type FilterBarChipProps } from '@/storybook/components/FilterBar/FilterBar';
import { translateDynamicKey } from '@/types/translation';
import { type AppliedFilterSet } from '@/utils/filter/appliedFilterSetStorage';
import { collapseTreeSelectValues } from '@/utils/filter/collapseTreeSelectValues';
import { isFilterType } from '@/utils/filter/isFilterType';
import { isRecord } from '@/utils/filter/isRecord';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';

interface UseActiveLocalChipsParams {
  currentLocalFilters: Record<string, Record<string, unknown>>;
  appliedSet: AppliedFilterSet | null;
  setIntact: boolean;
}

/**
 * Resolves the stored filter values for a given section/channel/filterKey path
 * and returns them as a Set for O(1) lookups. Returns null if the path doesn't exist.
 */
const resolveStoredValues = (
  appliedSet: AppliedFilterSet,
  sectionKey: string,
  channelKey: string | undefined,
  filterKey: string,
): Set<string> | null => {
  const storedLocal = appliedSet.localFilters;
  if (!isRecord(storedLocal)) return null;

  const sectionData = storedLocal[sectionKey];
  if (!isRecord(sectionData)) return null;

  const container = channelKey ? sectionData[channelKey] : sectionData;
  if (!isRecord(container)) return null;

  const values = container[filterKey];
  if (!Array.isArray(values)) return null;

  return new Set(values);
};

const useActiveLocalChips = ({
  currentLocalFilters,
  appliedSet,
  setIntact,
}: UseActiveLocalChipsParams): FilterBarChipProps[] => {
  const { t } = useTranslation();
  const { pathname } = useLocation();

  const handleDeleteLocalFilter = useCallback(
    (sectionKey: string, channelKey: string | undefined, filterKey: string, filterValue: string) => {
      const storageKey = LOCAL_FILTERS_STORAGE_MAP[sectionKey];
      if (!storageKey) return;

      try {
        const raw = sessionStorage.getItem(storageKey);
        if (!raw) return;
        const parsed = JSON.parse(raw);

        if (channelKey && typeof parsed[channelKey] === 'object') {
          const channelFilters = parsed[channelKey];
          if (Array.isArray(channelFilters[filterKey])) {
            channelFilters[filterKey] = channelFilters[filterKey].filter((stored: string) => stored !== filterValue);
          }
        } else if (Array.isArray(parsed[filterKey])) {
          parsed[filterKey] = parsed[filterKey].filter((stored: string) => stored !== filterValue);
        }

        syncSessionStorage(storageKey, parsed);
      } catch {
        // Non-critical: malformed sessionStorage entry, filter bar will re-sync on next render
      }
    },
    [],
  );

  return useMemo((): FilterBarChipProps[] => {
    const activeSectionKey = getActiveSectionKey(pathname);
    if (!activeSectionKey) return [];

    const sectionData = currentLocalFilters[activeSectionKey];
    if (!sectionData) return [];

    const isChannelNested = CHANNEL_NESTED_KEYS.has(activeSectionKey);
    const activeChannel = getActiveChannelKey(pathname);
    const chips: FilterBarChipProps[] = [];

    const buildChipsForFilter = (
      filterKey: string,
      filterValues: unknown[],
      channelKey: string | undefined,
      tooltip: string,
    ) => {
      const stringValues = filterValues.filter((filterValue): filterValue is string => typeof filterValue === 'string');
      const storedSet =
        appliedSet && setIntact ? resolveStoredValues(appliedSet, activeSectionKey, channelKey, filterKey) : null;
      const activeValues = storedSet ? stringValues.filter(filterValue => !storedSet.has(filterValue)) : stringValues;
      if (activeValues.length === 0) return;

      const collapsed = collapseTreeSelectValues(activeValues);
      for (const group of collapsed) {
        const idSuffix = channelKey ? `${channelKey}-${filterKey}` : filterKey;
        chips.push({
          'id': `local-${activeSectionKey}-${idSuffix}-${group.originalValues[0]}`,
          'label': group.label,
          'tooltip': tooltip,
          'onCloseClick': () => {
            for (const original of group.originalValues) {
              handleDeleteLocalFilter(activeSectionKey, channelKey, filterKey, original);
            }
          },
          'data-testid': `active-local-filter-${filterKey}`,
        });
      }
    };

    if (isChannelNested) {
      for (const [channelKey, channelFilters] of Object.entries(sectionData)) {
        if (!isRecord(channelFilters)) continue;
        if (activeChannel && channelKey !== activeChannel) continue;
        const channelLabel = getChannelLabel(channelKey, t);

        for (const [filterKey, filterValues] of Object.entries(channelFilters)) {
          if (!Array.isArray(filterValues)) continue;
          const configLabel = isFilterType(filterKey)
            ? translateDynamicKey(t, FILTER_CONFIG[filterKey].label)
            : filterKey;
          buildChipsForFilter(filterKey, filterValues, channelKey, `${channelLabel}: ${configLabel}`);
        }
      }
    } else {
      for (const [filterKey, filterValues] of Object.entries(sectionData)) {
        if (!Array.isArray(filterValues)) continue;
        const configLabel = isFilterType(filterKey)
          ? translateDynamicKey(t, FILTER_CONFIG[filterKey].label)
          : filterKey;
        buildChipsForFilter(filterKey, filterValues, undefined, configLabel);
      }
    }

    return chips;
  }, [pathname, currentLocalFilters, appliedSet, setIntact, t, handleDeleteLocalFilter]);
};

export default useActiveLocalChips;
